'use client';

import { useState } from 'react';
import { Mail, RotateCcw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface StepCheckEmailProps {
  email: string;
  onResend: () => Promise<void>;
  onBack: () => void;
}

/**
 * Interstitial shown after step 1 (Account) is submitted to
 * `/api/register/start`. The original tab stops here — the user opens
 * the verification email, clicks the link, which lands them in a fresh
 * tab at `/signup/verify/[token]` → redirected to `/signup?token=...`
 * → wizard re-mounts at step 2 (Charity).
 *
 * "Use a different email" sends them back to step 1 to edit. "Resend"
 * re-submits the same email/password to `/api/register/start` to mint
 * a fresh token (the upsert behaviour replaces the previous one).
 */
export function StepCheckEmail({ email, onResend, onBack }: StepCheckEmailProps) {
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<Date | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  async function handleResend() {
    setResending(true);
    setResendError(null);
    try {
      await onResend();
      setResentAt(new Date());
    } catch (err) {
      setResendError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setResending(false);
    }
  }

  return (
    <div className="space-y-6 py-4">
      <div className="flex flex-col items-center text-center">
        <div className="rounded-full bg-blue-50 p-3 mb-4">
          <Mail className="h-8 w-8 text-blue-600" />
        </div>
        <h2 className="text-xl font-semibold">Check your email</h2>
        <p className="text-sm text-gray-600 mt-2 max-w-sm">
          We sent a verification link to <strong>{email}</strong>. Open it and
          click <strong>Verify my email</strong> to continue setting up your
          charity.
        </p>
      </div>

      <Alert>
        <AlertDescription className="text-sm">
          <strong>Don&apos;t see it?</strong> Check your spam or junk folder. If
          it&apos;s there, mark it as <em>Not spam</em> so future emails from
          TrusteeKit (donor receipts, claim updates) reach your inbox.
        </AlertDescription>
      </Alert>

      {resentAt && (
        <Alert>
          <AlertDescription className="text-sm">
            Verification email re-sent at {resentAt.toLocaleTimeString('en-GB')}.
          </AlertDescription>
        </Alert>
      )}

      {resendError && (
        <Alert variant="destructive">
          <AlertDescription className="text-sm">{resendError}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={resending}
          className="flex-1"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Use a different email
        </Button>
        <Button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="flex-1"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          {resending ? 'Sending…' : 'Resend email'}
        </Button>
      </div>

      <p className="text-xs text-center text-gray-500">
        After clicking the link, the rest of the signup will open in a new tab.
        You can close this one.
      </p>

      <p className="text-xs text-center text-gray-500">
        Still stuck?{' '}
        <a
          href="mailto:%22TrusteeKit%20Help%22%20%3Cinfo%40trusteekit.co.uk%3E?subject=Help%20with%20TrusteeKit"
          className="text-blue-600 hover:underline"
        >
          info@trusteekit.co.uk
        </a>
      </p>
    </div>
  );
}
